/**
 * `GET /api/referinta` — payloadul de bootstrap.
 *
 * Un singur round-trip la deschidere, cu tot ce e stabil: valute, banci, catalogul de stari,
 * conturile de trezorerie, setarile, fereastra de calendar bancar si utilizatorul curent cu rolul
 * lui. Alternativa (opt cereri mici) ar pune opt round-trips inaintea primului ecran util.
 *
 * `staleTime` e practic infinit, si asta e o decizie de produs, nu o optimizare: nomenclatoarele se
 * schimba de cateva ori pe an, iar o reincarcare a lor sub cursor ar reordona conturile din tabelul
 * de cashflow exact in secunda in care cineva tasteaza intr-o celula. Cand chiar se schimba ceva
 * (un cont nou, o zi de calendar), pagina de setari invalideaza explicit `chei.referinta.tot()`.
 *
 * Calendarul intra tot aici pentru ca navigarea `Alt+←/→` si amanarea „ziua lucratoare urmatoare"
 * trebuie sa raspunda instant si sa dea ACELASI rezultat ca serverul — deci clientul primeste
 * aceleasi randuri, nu un algoritm propriu care ar putea diverge la prima sarbatoare legala.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import { api } from '../lib/api.ts';
import { chei } from '../lib/query-keys.ts';

/**
 * Raspunsul rutei nu are schema Zod in `functions/api/schemas.ts` (sectiunea „Raspunsuri" acopera
 * doar ecranele mari), deci forma e descrisa aici, camp cu camp, dupa `routes/reference.ts`.
 */
export interface Valuta {
  code: string;
  nameRo: string;
  symbol: string | null;
  /** Cate unitati minore intra intr-o unitate majora. Banii NU se convertesc in float niciodata. */
  minorScale: number;
  bnrMultiplier: number;
  isActive: boolean;
  sortOrder: number;
}

export interface Banca {
  id: number;
  code: string;
  name: string;
  ibanBankCode: string | null;
  isFxProvider: boolean;
  isReference: boolean;
  portalUrl: string | null;
  fxColumnOrder: number | null;
  sortOrder: number;
}

export interface StareCatalog {
  code: string;
  labelRo: string;
  descriptionRo: string | null;
  shortCode: string;
  numericAlias: number | null;
  glyph: string;
  argb: string | null;
  isApproved: boolean;
  blocksPayment: boolean;
  isTerminal: boolean;
  countsInTotal: boolean;
  autopostponeEligible: boolean;
  sortOrder: number;
}

export type GrupCont = 'Conturi' | 'Linii de credit' | 'Non-bancar';

export interface ContTrezorerie {
  id: number;
  bankId: number | null;
  bankCode: string | null;
  label: string;
  currency: string;
  rowKind: string;
  group: GrupCont;
  isCreditLine: boolean;
  creditLimitMinor: number | null;
  creditLimitCurrency: string | null;
  maturityDate: string | null;
  maturityNote: string | null;
  portalUrl: string | null;
  legalEntity: string;
  inAvailableRon: boolean;
  inAvailableFx: boolean;
  inAccountTotal: boolean;
  derivedExpr: string | null;
  sortOrder: number;
}

export interface ZiCalendar {
  calDate: string;
  isBusinessDay: boolean;
  isPublicHoliday: boolean;
  holidayName: string | null;
  isSettlementOpen: boolean;
  note: string | null;
  source: string;
}

export type RolUtilizator = 'viewer' | 'operator' | 'admin';

export interface UtilizatorCurent {
  id: number;
  email: string;
  displayName: string;
  role: RolUtilizator;
  identityKind: string;
  sessionExpiresAt: number | null;
}

export interface RaspunsReferinta {
  today: string;
  todayLabel: string;
  window: { from: string; to: string };
  user: UtilizatorCurent;
  currencies: Valuta[];
  banks: Banca[];
  statuses: StareCatalog[];
  cashAccounts: ContTrezorerie[];
  cashAccountGroups: Record<GrupCont, number[]>;
  settings: Record<string, unknown>;
  settingLabels: Record<string, string | null>;
  calendar: ZiCalendar[];
  calendarCoverage: { first: string | null; last: string | null };
  /** Non-null cand calendarul nu acopera urmatoarele 60 de zile: amanarile in gol ar EȘUA. */
  calendarWarning: string | null;
  categories: Array<{ id: number; name: string; supplierCount: number; sortOrder: number }>;
  runLabels: Array<{
    labelNorm: string;
    labelRaw: string;
    weekdayRo: string | null;
    operatorLabel: string | null;
    legalEntity: string;
    kind: string;
    occurrences: number;
    confirmed: boolean;
  }>;
}

export interface FereastraReferinta {
  from?: string;
  to?: string;
}

export function useReferinta(
  fereastra?: FereastraReferinta,
  optiuni?: { activ?: boolean },
): UseQueryResult<RaspunsReferinta, Error> {
  return useQuery({
    queryKey: chei.referinta.fereastra(fereastra),
    // Se cere doar dupa ce stim ca exista sesiune (`Shell` pasează `activ`). Altfel fiecare
    // incarcare a paginii de login ar produce un al doilea 401, degeaba.
    enabled: optiuni?.activ !== false,
    queryFn: ({ signal }) =>
      api.get<RaspunsReferinta>('/referinta', {
        interogare: { from: fereastra?.from, to: fereastra?.to },
        semnal: signal,
      }),
    // Nomenclatoarele nu se schimba in timpul unei sesiuni de lucru. `Infinity` face explicit ca
    // reimprospatarea e o DECIZIE (butonul din /setari), nu un efect secundar al navigarii.
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
}

/**
 * `GET /api/eu` — cine sunt si ce rol am, fara restul bootstrapului.
 *
 * Se foloseste dupa ce o cerere a intors 401: raspunsul spune daca sesiunea Access s-a reinnoit
 * intre timp (utilizatorul a reincarcat intr-un alt tab) sau chiar trebuie reincarcata pagina.
 */
export interface RaspunsEu extends UtilizatorCurent {
  serverTime: number;
  /** Cat timp e adevarat, `Shell` arata ecranul de schimbare a parolei si nimic altceva. */
  mustChangePassword: boolean;
  /** Pana cand se pot afisa secrete bancare fara a cere din nou parola. */
  stepupUntil: number | null;
}

export function useEu(activ = true): UseQueryResult<RaspunsEu, Error> {
  return useQuery({
    queryKey: chei.eu(),
    queryFn: ({ signal }) => api.get<RaspunsEu>('/eu', { semnal: signal }),
    enabled: activ,
    staleTime: 60_000,
    // Fara reincercare: daca sesiunea a picat, insistenta nu o reinvie, doar intarzie mesajul.
    retry: false,
  });
}

/** Ziua din calendar, cautata local. Serverul si clientul folosesc ACELASI set de randuri. */
export function ziDinCalendar(
  referinta: RaspunsReferinta | undefined,
  data: string,
): ZiCalendar | undefined {
  return referinta?.calendar.find((zi) => zi.calDate === data);
}

/**
 * Urmatoarea zi lucratoare, DIN CALENDARUL POPULAT — niciodata calculata.
 *
 * Pastele ortodox si Rusaliile sunt mobile, iar Guvernul adauga punți prin hotarare: niciun
 * algoritm nu e complet. Cand calendarul nu acopera intervalul, raspunsul e `null` si interfata
 * dezactiveaza butonul, in loc sa ghiceasca o data pe care banca nu o deconteaza.
 */
export function ziuaLucratoareUrmatoare(
  referinta: RaspunsReferinta | undefined,
  data: string,
): string | null {
  if (!referinta) return null;
  // `calendar` vine deja ordonat crescator din ruta; nu presupunem asta si comparam sirurile ISO,
  // care se ordoneaza lexicografic corect.
  let cea_mai_apropiata: string | null = null;
  for (const zi of referinta.calendar) {
    if (zi.calDate <= data || !zi.isBusinessDay) continue;
    if (cea_mai_apropiata === null || zi.calDate < cea_mai_apropiata) cea_mai_apropiata = zi.calDate;
  }
  return cea_mai_apropiata;
}

/** Rolul decide ce se poate SCRIE; Access decide doar cine intra. */
export function poateScrie(referinta: RaspunsReferinta | undefined): boolean {
  const rol = referinta?.user.role;
  return rol === 'operator' || rol === 'admin';
}

export function esteAdmin(referinta: RaspunsReferinta | undefined): boolean {
  return referinta?.user.role === 'admin';
}
